// CommunityScreen.tsx — 솟다 커뮤니티 탭 본 화면 (실 RPC 배선)
// 2026-06-14 개편: mock 5글 → community_posts 실 조회 + attachLevels(레벨·아바타·봇구분).
// 필터칩=실 카테고리(전체/자유/질문/정보/유머/시장/투자) · 검색=ilike · 모든 글 클릭 → /community/post/:id.
// FAB → /community/write. design doc: 2026-06-14-sotda-ia-ux-redesign §2.
// @ts-nocheck
import '../cs-tokens.css';
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useActiveMacroEvents, pickPrimaryEvent } from '../../hooks/useMacroEvents';
import { CSCard, CSAppBar, CSBadge, CSIconBtn, CSIcon } from '../_components/cs';
import { SkeletonCard } from '../../components/common/Skeleton';
import { CastAvatar, MBTI_SHORT_LABEL } from '../_components/cast';
import { supabase } from '../../lib/supabase';
import { CATEGORIES as BASE_CATEGORIES, PER_PAGE, formatPostDate } from '@core/community/communityMeta';
import { postExcerpt } from '@core/community/postExcerpt';
import { attachLevels } from '../../data-access/authorLevelsAdapter';
import { getBlockedAuthors } from '../../data-access/blockListAdapter';
import { useAuth } from '../../lib/useAuth';
import { IndicatorGalleryList } from './IndicatorGalleryList';
import { STUDIO_HOME_PATH, STUDIO_MY_INDICATORS_PATH } from './studio/studioRoutes';
import { AvatarProfileButton, BotAuthorAvatar, CosmeticIdentityTrigger, cosmeticFromAuthor } from '../_components/SocialCosmetic';
import { POST_COLUMNS } from '@core/community/columns';

import { USER_BRIEFING_ENABLED } from '../../lib/briefingFeature';
import { TodayBriefingSection } from './TodayBriefingSection';
const CATEGORIES = USER_BRIEFING_ENABLED ? [...BASE_CATEGORIES, '브리핑'] : BASE_CATEGORIES;

// 지금 인기 섹션 — 최근 24h 유저글 인기점수(좋아요·댓글·조회 가중) 상위 가로 랭킹.
// 시안 이식(2026-07-13): 1위=딥그린 히어로 filled·나머지=아이보리 카드(무테두리·§G).
function HotSection({ posts, onOpen }) {
  if (!posts || posts.length === 0) return null;
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, padding: '0 16px 10px' }}>
        <span className="cs-italic" style={{ fontSize: 16 }}>지금 인기</span>
        <span style={{ fontSize: 11, color: 'var(--cs-mute)', fontFamily: 'var(--cs-font-mono)' }}>최근 24시간</span>
      </div>
      <div style={{ display: 'flex', gap: 10, padding: '0 16px', overflowX: 'auto', scrollbarWidth: 'none' }}>
        {posts.map((p, i) => {
          const filled = i === 0;
          return (
            <div key={p.id} onClick={() => onOpen(p.id)} role="button"
              style={{
                flex: '0 0 auto', width: '76%', maxWidth: 280, padding: 14, borderRadius: 'var(--cs-r-lg)',
                background: filled ? 'var(--cs-hero-ink)' : 'var(--cs-surface)',
                color: filled ? 'var(--cs-on-ink)' : 'var(--cs-ink)',
                boxShadow: filled ? 'var(--cs-shadow-hero)' : 'none', cursor: 'pointer', // 히어로 = 1위 잉크 카드 1개만(V1)
              }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <span style={{ fontSize: 18, fontWeight: 800, fontFamily: 'var(--cs-font-serif)',
                  color: filled ? 'var(--cs-gold-on-ink)' : 'var(--cs-accent)' }}>{i + 1}</span>
                <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 999,
                  background: filled ? 'rgba(255,255,255,0.14)' : 'var(--cs-surface-2)',
                  color: filled ? 'var(--cs-on-ink)' : 'var(--cs-mute)' }}>{p.category}</span>
              </div>
              <div style={{ fontSize: 13.5, fontWeight: 700, lineHeight: 1.4, letterSpacing: '-0.02em',
                display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', minHeight: 38 }}>
                {p.title}
              </div>
              <div style={{ display: 'flex', gap: 12, marginTop: 10, fontSize: 11,
                color: filled ? 'rgba(241,236,224,0.75)' : 'var(--cs-mute)', fontFamily: 'var(--cs-font-mono)' }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                  <CSIcon name="heart" size={11} color={filled ? 'var(--cs-on-ink)' : 'var(--cs-mute)'} /> {p.likes || 0}
                </span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                  <CSIcon name="chat" size={11} color={filled ? 'var(--cs-on-ink)' : 'var(--cs-mute)'} /> {p.comment_count || 0}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// 본문 발췌 — 마크다운·HTML 기호 제거는 @core/community/postExcerpt (D-6② 회귀 테스트 있음)
function excerptOf(p) {
  return postExcerpt(p.content);
}

// 조회수 표기 (1000+ → 1.2K)
function fmtCount(n) {
  const v = Number(n) || 0;
  return v >= 1000 ? `${(v / 1000).toFixed(1)}K` : String(v);
}

export function SotdaCommunityScreen() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [posts, setPosts] = useState([]);
  const [cat, setCat] = useState('전체');
  const [section, setSection] = useState('posts');
  // 거시 이벤트방(6엔진 Step 4) 마감임박 칩 — voting 이벤트 있을 때만(토글 OFF면 [] → 숨김)
  const macroPrimary = pickPrimaryEvent(useActiveMacroEvents());
  const macroVoting = macroPrimary && macroPrimary.status === 'voting' ? macroPrimary : null;
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchOpen, setSearchOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [searchField, setSearchField] = useState('title'); // title|content|author
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const reqGen = useRef(0);
  // 차단 작성자(로컬) — 피드에서 숨김
  const [blocked, setBlocked] = useState(() => new Set());
  useEffect(() => { getBlockedAuthors(user?.id).then((s) => setBlocked(new Set(s))).catch(() => {}); }, [user?.id]);

  // 봇 작성자 id — 피드 쿼리에서 봇글을 떼어내기 위해 1회 조회 (현재 황둥이·삐용이 2계정).
  // 🔴 D-6①(2026-09-01 SE3 실기): community_posts에는 봇 여부 컬럼이 없다(is_bot은 profiles 조인).
  //   그래서 예전엔 봇글까지 20건씩 페이지네이션한 뒤 화면에서 페르소나별 1건만 남겼고,
  //   "더 보기 (40/971)"이 화면의 2건과 무관한 숫자를 말했다(실측: 971건 중 유저글은 13건).
  //   더보기를 눌러도 봇글만 더 실려 화면이 그대로였다 → 버튼이 고장 난 것처럼 보였다.
  //   이제 목록은 **유저글만** 페이지네이션하고, 봇글은 페르소나별 최신 1건만 따로 얹는다.
  //   null = 아직 미로드(첫 조회 보류) · [] = 조회 실패나 봇 없음(옛 동작으로 폴백)
  const [botIds, setBotIds] = useState(null);
  const [botPosts, setBotPosts] = useState([]);
  useEffect(() => {
    (async () => {
      const { data, error } = await supabase.from('profiles').select('id, bot_persona').eq('is_bot', true);
      if (error) throw error;
      setBotIds(data || []);
    })().catch((e) => {
      // 실패해도 화면은 떠야 한다 — 봇 분리 없이 조회하고 displayPosts의 dedupe이 받아준다.
      console.warn('[community] bot ids fetch failed', e?.message);
      setBotIds([]);
    });
  }, []);

  // 지금 인기(시안 이식) — 최근 24h 유저글 인기점수 상위(좋아요×3+댓글×2+조회×0.05). 봇글 제외·클라 후정렬(RPC 불요).
  const [hotPosts, setHotPosts] = useState([]);
  // ⚠ S-14 (2026-07-30): 인기 섹션에 차단 필터가 없어, 차단한 사람의 글이 상위 6개에 들면
  //   일반 피드 위에 그대로 노출되고 클릭도 됐다("보이지 않아요"라는 약속을 같은 화면에서 어겼다).
  //   피드(아래 filter)와 **같은 기준**으로 거른다 — 본인 글은 차단 대상이 아니다.
  useEffect(() => {
    (async () => {
      const since = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
      const { data } = await supabase.from('community_posts')
        .select(POST_COLUMNS).gte('created_at', since)
        .order('likes', { ascending: false }).limit(20);
      const scored = (data || [])
        .filter((p) => !p.author_is_bot)
        .filter((p) => !(p.author_id && p.author_id !== user?.id && blocked.has(p.author_id)))
        .map((p) => ({ ...p, _hot: (p.likes || 0) * 3 + (p.comment_count || 0) * 2 + (p.views || 0) * 0.05 }))
        .sort((a, b) => b._hot - a._hot).slice(0, 6);
      setHotPosts(scored);
    })().catch(() => {});
    // blocked가 나중에 로드되면 인기 목록도 다시 걸러야 한다(차단 직후 즉시 반영).
  }, [blocked, user?.id]);

  // 쿼리에서 봇 작성자 글을 제외한다.
  // ⚠ `not.in`만 쓰면 author_id가 NULL인 익명 글까지 사라진다(SQL NOT IN + NULL = NULL).
  //   그래서 `author_id.is.null` 을 OR로 함께 건다.
  const excludeBots = useCallback((q) => {
    const ids = (botIds || []).map((b) => b.id).filter(Boolean);
    if (ids.length === 0) return q;
    return q.or(`author_id.is.null,author_id.not.in.(${ids.join(',')})`);
  }, [botIds]);

  // 필터칩 카테고리별 카운트(시안 이식) — head:true count 병렬(본문 미전송·경량). 검색/필터 무관 고정.
  const [catCounts, setCatCounts] = useState({});
  useEffect(() => {
    (async () => {
      const entries = await Promise.all(CATEGORIES.map(async (c) => {
        let q = supabase.from('community_posts').select('id', { count: 'exact', head: true });
        if (c !== '전체') q = q.eq('category', c);
        // D-6①: 칩 숫자도 목록과 같은 모집단(유저글)이어야 한다. 봇글 958건이 섞이면
        // '전체 971'처럼 화면에 없는 글까지 세는 숫자가 된다.
        q = excludeBots(q);
        const { count } = await q;
        return [c, count || 0];
      }));
      setCatCounts(Object.fromEntries(entries));
    })().catch(() => {});
  }, [excludeBots]);

  // 검색 디바운스 — 매 키입력 재조회/응답 뒤섞임 방지 (300ms)
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const fetchPosts = useCallback(async (pageArg, reset) => {
    const gen = ++reqGen.current; // in-flight 세대 토큰 — 옛 응답이 새 응답 덮어쓰는 것 방지
    setLoading(true);
    try {
      let q = supabase.from('community_posts').select(POST_COLUMNS, { count: 'exact' });
      if (cat !== '전체') q = q.eq('category', cat);
      const s = debouncedSearch.trim();
      if (s) q = q.ilike(searchField, `%${s}%`);
      // D-6①: 봇글은 아래 별도 effect가 페르소나별 최신 1건만 가져온다.
      //   여기서 빼야 count/hasMore가 화면에 보이는 글 수와 일치한다.
      q = excludeBots(q);
      const { data, count, error } = await q
        .order('created_at', { ascending: false })
        .range(pageArg * PER_PAGE, (pageArg + 1) * PER_PAGE - 1);
      if (error) throw error;
      if (gen !== reqGen.current) return; // 더 최신 요청이 떴으면 이 응답 폐기
      const enriched = await attachLevels(data || []);
      if (gen !== reqGen.current) return;
      setPosts((prev) => (reset ? enriched : [...prev, ...enriched]));
      setTotal(count || 0);
    } catch (e) {
      console.warn('[community] fetch failed', e?.message);
      if (reset && gen === reqGen.current) setPosts([]);
    } finally {
      if (gen === reqGen.current) setLoading(false);
    }
  }, [cat, debouncedSearch, searchField, excludeBots]);

  // 카테고리/검색(디바운스) 변경 시 첫 페이지 재조회.
  // ⚠ botIds 미로드(null) 동안은 조회를 미룬다 — 봇 제외 없이 한 번 긁으면 971건 기준 목록이
  //   잠깐 뜬 뒤 교체되면서 카운트가 튄다.
  useEffect(() => {
    if (botIds === null) return;
    setPage(0);
    fetchPosts(0, true);
  }, [cat, debouncedSearch, fetchPosts, botIds]);

  // 봇글 — 페르소나별 최신 1건씩만. 카테고리·검색 필터는 목록과 동일하게 적용한다.
  useEffect(() => {
    if (!botIds || botIds.length === 0) { setBotPosts([]); return; }
    (async () => {
      const s = debouncedSearch.trim();
      const rows = await Promise.all(botIds.map(async (b) => {
        let q = supabase.from('community_posts').select(POST_COLUMNS).eq('author_id', b.id);
        if (cat !== '전체') q = q.eq('category', cat);
        if (s) q = q.ilike(searchField, `%${s}%`);
        const { data } = await q.order('created_at', { ascending: false }).limit(1);
        return (data || [])[0] || null;
      }));
      const found = rows.filter(Boolean);
      setBotPosts(found.length ? await attachLevels(found) : []);
    })().catch((e) => {
      console.warn('[community] bot posts fetch failed', e?.message);
      setBotPosts([]);
    });
  }, [botIds, cat, debouncedSearch, searchField]);

  const loadMore = () => {
    const np = page + 1;
    setPage(np);
    fetchPosts(np, false);
  };

  // total·posts 둘 다 이제 유저글만 센다 → 더보기 버튼과 화면이 어긋나지 않는다(D-6①).
  const hasMore = posts.length < total;

  // 화면에 얹을 봇글 — 페르소나별 최신 1건 + 삐용이 속보 TTL. (봇 분리 조회 성공 경로)
  const visibleBots = useMemo(() => {
    const now = Date.now();
    const EVENT_TTL = 6 * 60 * 60 * 1000; // 삐용이 속보 6시간 후 자동 숨김
    return (botPosts || []).filter((p) => {
      if (p.author_bot_persona === 'event' && now - new Date(p.created_at).getTime() > EVENT_TTL) return false;
      return true;
    });
  }, [botPosts]);

  // 봇글 도배 방지 — 피드에 봇글은 최신 1개만 노출(유저글은 전부). 초기 유저 부재기 이미지 보호
  // ⚠ botIds 조회 실패 폴백에서는 posts에 봇글이 섞여 들어오므로 이 dedupe을 그대로 남긴다.
  const userPosts = useMemo(() => {
    const now = Date.now();
    const EVENT_TTL = 6 * 60 * 60 * 1000; // 삐용이 속보 6시간 후 자동 숨김
    let analystShown = false, eventShown = false;
    return posts.filter((p) => {
      // 차단 작성자 숨김 — 단 본인 글은 항상 노출(차단은 디바이스 로컬이라 계정 전환 시 본인 글 사라짐 방지)
      if (p.author_id && p.author_id !== user?.id && blocked.has(p.author_id)) return false;
      if (p.author_is_bot) {
        if (p.author_bot_persona === 'event') {
          // 삐용이 속보 — 최신 1개 + TTL 경과 시 숨김
          if (eventShown) return false;
          if (now - new Date(p.created_at).getTime() > EVENT_TTL) return false;
          eventShown = true;
        } else {
          // 황둥이 정기글 — 최신 1개로 갱신
          if (analystShown) return false;
          analystShown = true;
        }
      }
      return true;
    });
  }, [posts, blocked, user]);

  // 최종 목록 = 봇글(최신 1건씩) + 유저글, created_at 내림차순 병합.
  const displayPosts = useMemo(() => {
    const bots = visibleBots.filter((p) => !(p.author_id && p.author_id !== user?.id && blocked.has(p.author_id)));
    return [...bots, ...userPosts].sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
    );
  }, [visibleBots, userPosts, blocked, user]);

  return (
    <div className="cs-root" data-density="regular" data-temp="warm" data-heading="serif"
         style={{ width: '100%', minHeight: '100%', position: 'relative', background: 'var(--cs-bg)' }}>
      <CSAppBar
        left={<span className="cs-italic" style={{ fontSize: 19 }}>커뮤니티</span>}
        title=""
        right={<CSIconBtn label="검색" onClick={() => setSearchOpen((v) => !v)}><CSIcon name="search" /></CSIconBtn>}
      />

      <div role="tablist" aria-label="커뮤니티 보기" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4, margin: '0 16px 12px', padding: 4, borderRadius: 999, background: 'var(--cs-surface-2)' }}>
        {[['posts', '글'], ['indicators', '지표']].map(([key, label]) => <button key={key} role="tab" aria-selected={section === key} onClick={() => setSection(key)} style={{ border: 0, borderRadius: 999, padding: '8px 12px', background: section === key ? 'var(--cs-surface)' : 'transparent', color: section === key ? 'var(--cs-ink)' : 'var(--cs-mute)', fontWeight: 800, cursor: 'pointer', boxShadow: section === key ? 'var(--cs-shadow-1)' : 'none' }}>{label}</button>)}
      </div>

      {section === 'indicators' ? <>
        <div style={{ padding: '0 16px 10px' }}>
          <button type="button" onClick={() => navigate(STUDIO_HOME_PATH)}
            style={{ width: '100%', minHeight: 44, border: 0, borderRadius: 'var(--cs-r-md)', background: 'var(--cs-accent)', color: 'var(--cs-accent-ink)', fontSize: 13, fontWeight: 800, cursor: 'pointer' }}>나만의 지표 만들기</button>
          {user && <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button type="button" onClick={() => navigate(STUDIO_MY_INDICATORS_PATH)}
              style={{ border: 0, background: 'transparent', color: 'var(--cs-accent)', fontSize: 12, fontWeight: 800, cursor: 'pointer', minHeight: 44, padding: '0 4px' }}>내가 올린 지표 ›</button>
          </div>}
        </div>
        <IndicatorGalleryList />
      </> : <>

      {/* 검색바 (토글) */}
      {searchOpen && (
        <div style={{ padding: '0 16px 10px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px',
                        background: 'var(--cs-surface-2)', borderRadius: 12 }}>
            <CSIcon name="search" size={16} color="var(--cs-mute)" />
            <input
              autoFocus value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder={`${searchField === 'author' ? '닉네임' : searchField === 'content' ? '내용' : '제목'}으로 검색`}
              style={{ flex: 1, border: 0, outline: 'none', background: 'transparent',
                       fontSize: 13.5, color: 'var(--cs-ink)' }}
            />
            {search && (
              <button onClick={() => setSearch('')}
                      style={{ border: 0, background: 'transparent', cursor: 'pointer', color: 'var(--cs-mute)', padding: '2px 6px', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}>
                지우기
              </button>
            )}
          </div>
          {/* 검색 대상 필드 선택 */}
          <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
            {[{ k: 'title', l: '제목' }, { k: 'content', l: '내용' }, { k: 'author', l: '닉네임' }].map((f) => {
              const on = searchField === f.k;
              return (
                // 검색 대상 칩도 카테고리 칩과 같은 규칙(테두리 0 · 면 대비 + 그림자) — §G-2
                <button key={f.k} onClick={() => setSearchField(f.k)} style={{
                  padding: '5px 13px', fontSize: 11.5, fontWeight: on ? 700 : 500, border: 0,
                  background: on ? 'var(--cs-surface)' : 'var(--cs-surface-2)',
                  color: on ? 'var(--cs-ink)' : 'var(--cs-mute)',
                  boxShadow: on ? 'var(--cs-shadow-1)' : 'none',
                  borderRadius: 999, cursor: 'pointer',
                }}>{f.l}</button>
              );
            })}
          </div>
        </div>
      )}

      {/* 거시 이벤트방 마감임박 칩 — 금리 예측 투표 진입 (voting 한정) */}
      {macroVoting && (
        <div onClick={() => navigate(`/macro-event/${macroVoting.id}`)}
          style={{ margin: '0 12px 8px', padding: '10px 14px', background: 'var(--cs-gold-soft)', borderRadius: 'var(--cs-r-md)', display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
          <span style={{ flexShrink: 0, display: 'inline-flex' }}><CSIcon name="building" size={16} color="var(--cs-gold)" /></span>
          <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, fontWeight: 700, color: 'var(--cs-ink)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{macroVoting.title} · 금리 예측</span>
          <span style={{ fontSize: 11, fontWeight: 800, color: 'var(--cs-gold)', flexShrink: 0 }}>{macroVoting.my_pick ? '예측함 →' : '투표 →'}</span>
        </div>
      )}

      {/* 필터칩 — 실 카테고리
          ⚠ 2026-07-26 디자인 언어 통일 (효신님 ① "세그먼트와 바로 밑 카테고리 UI가 어긋난다")
            BEFORE: 위 [글|지표] 세그먼트 = 채운 pill(surface-2 트랙 + 활성 surface + 그림자, 테두리 0)
                    아래 카테고리 칩 = 1px 테두리 + 투명 배경 → 세로로 붙어 있는데 언어가 선/면으로 갈렸다.
            AFTER : 칩도 테두리 제거 → surface-2 배경 · 활성은 surface + shadow-1로 떠오름(세그먼트와 동일 규칙).
                    docs/design.md §G-2 "카드에 border 지양 · 경계는 배경 명도 대비 + 그림자" 준수.
            위계는 색이 아니라 크기·굵기로: 세그먼트 13px/800 vs 칩 12px/700. */}
      <div style={{ display: 'flex', gap: 6, padding: '4px 12px 12px', overflowX: 'auto' }}>
        {CATEGORIES.map((c) => {
          const active = cat === c;
          return (
            <button key={c} onClick={() => setCat(c)} style={{
              flex: '1 0 auto', minWidth: 0, border: 0,
              padding: '7px 13px', fontSize: 12, fontWeight: active ? 700 : 500,
              background: active ? 'var(--cs-surface)' : 'var(--cs-surface-2)',
              color: active ? 'var(--cs-ink)' : 'var(--cs-mute)',
              boxShadow: active ? 'var(--cs-shadow-1)' : 'none',
              borderRadius: 999, cursor: 'pointer', whiteSpace: 'nowrap',
              letterSpacing: '-0.02em', transition: 'background var(--cs-dur-fast) ease, color var(--cs-dur-fast) ease',
            }}>{c}{catCounts[c] != null && (
              <span style={{ marginLeft: 5, fontSize: 10.5, fontWeight: 700, fontFamily: 'var(--cs-font-mono)',
                color: active ? 'var(--cs-accent)' : 'var(--cs-mute)' }}>{catCounts[c]}</span>
            )}</button>
          );
        })}
      </div>

      {USER_BRIEFING_ENABLED && !authLoading && section === 'posts' && cat === '전체' && !search && (
        <TodayBriefingSection key={user?.id || 'guest'} viewerId={user?.id || null} />
      )}

      {/* 지금 인기 — 전체 카테고리·검색없음일 때만 (필터/검색 시 숨김) */}
      {/* ⚠ 2026-07-26: 조건이 section === '글' 이었다. section 값은 'posts'|'indicators'(라벨이 아니라 키)라
          항상 false → 이 섹션이 한 번도 렌더되지 않았다. 라벨/키 혼동. */}
      {section === 'posts' && cat === '전체' && !debouncedSearch && (
        <HotSection posts={hotPosts} onOpen={(id) => navigate(`/community/post/${id}`)} />
      )}

      {/* posts */}
      <div className="cs-stagger" style={{ padding: '0 16px 24px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        {displayPosts.map((p) => {
          const isBot = !!p.author_is_bot;
          const botColor = p.author_bot_persona === 'event' ? 'var(--cs-alert)' : 'var(--cs-bull)';
          const excerpt = excerptOf(p);
          return (
            <CSCard key={p.id} padded onClick={() => navigate(`/community/post/${p.id}`)}
              style={{
                padding: 16,
                background: 'var(--cs-surface)',
                cursor: 'pointer',
              }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {/* 봇 아바타 = 둥근 사각(B안 2026-09-03) — 유저 아바타(원)와 실루엣으로 구분(색맹·mocha에서도) */}
                {isBot
                  ? <BotAuthorAvatar persona={p.author_bot_persona} author={p.author} size={36} />
                  : <AvatarProfileButton name={p.author} cosmetic={cosmeticFromAuthor(p)} size={36} />}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    {isBot
                      ? <span style={{ fontSize: 12.5, fontWeight: 700, color: botColor }}>@{p.author}</span>
                      : <CosmeticIdentityTrigger name={p.author} cosmetic={cosmeticFromAuthor(p)} fontSize={12.5} fontWeight={700} showMini={false} />}
                    {/* MBTI 배지 — 진단 완료한 일반 작성자만 (봇·익명·미진단 제외) */}
                    {!isBot && p.author_mbti && MBTI_SHORT_LABEL[p.author_mbti] && (
                      <CSBadge tone="neutral" style={{ fontSize: 9.5, padding: '2px 6px' }}>{MBTI_SHORT_LABEL[p.author_mbti]}</CSBadge>
                    )}
                    {/* 레벨은 아바타 우하단 메탈 도트로 이동(2026-09-03) — 닉 행은 [닉][칭호] 2요소 유지.
                        봇 AI 배지도 2026-09-09에 아바타 우하단 도트로 이동했다(닉 행 가로폭 확보). */}
                  </div>
                  <div style={{ fontSize: 10.5, color: 'var(--cs-mute)', fontFamily: 'var(--cs-font-mono)', marginTop: 1 }}>
                    {p.category}{p.subcategory ? ` · ${p.subcategory}` : ''} · {formatPostDate(p.created_at, new Date())}
                  </div>
                </div>
              </div>
              <h3 style={{ fontSize: 15.5, fontWeight: 700, letterSpacing: '-0.02em', marginTop: 10, lineHeight: 1.4 }}>{p.title}</h3>
              {excerpt && (
                <p data-post-body="excerpt" style={{ fontSize: 12.5, color: 'var(--cs-ink-2)', marginTop: 6, lineHeight: 1.55,
                            overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                  {excerpt}
                </p>
              )}
              <div style={{ display: 'flex', gap: 16, marginTop: 10, fontSize: 11, color: 'var(--cs-mute)', fontFamily: 'var(--cs-font-mono)', alignItems: 'center' }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><CSIcon name="heart" size={12} /> {p.likes || 0}</span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><CSIcon name="chat" size={12} /> {p.comment_count || 0}</span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><CSIcon name="eye" size={12} /> {fmtCount(p.views)}</span>
              </div>
            </CSCard>
          );
        })}

        {/* 로딩 스켈레톤 (첫 로드)
            ⚠ 2026-09-08: 인라인 border 박스 → 공통 SkeletonCard. 2026-07-26 "테두리 제거·펄스" 정리에서
              이 블록만 누락돼, 로딩이 끝나는 순간 카드 경계 표현이 테두리→그림자로 바뀌며 튀었다(§G-2). */}
        {loading && displayPosts.length === 0 && [0, 1, 2].map((i) => (
          <SkeletonCard key={`sk-${i}`} lines={2} />
        ))}

        {/* 빈 상태 */}
        {!loading && displayPosts.length === 0 && (
          <div style={{ marginTop: 32, padding: '32px 20px', textAlign: 'center' }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--cs-ink)' }}>
              {search ? '검색 결과가 없어요' : '아직 글이 없어요'}
            </div>
            <div style={{ fontSize: 12.5, color: 'var(--cs-mute)', marginTop: 6, lineHeight: 1.5 }}>
              {search ? '다른 키워드로 찾아보세요' : '첫 글을 남겨 커뮤니티를 시작해보세요'}
            </div>
            {!search && (
              <button onClick={() => navigate('/community/write')} style={{
                marginTop: 16, padding: '10px 20px', fontSize: 13, fontWeight: 700,
                background: 'var(--cs-ink)', color: 'var(--cs-bg)', border: 0, borderRadius: 999, cursor: 'pointer',
              }}>글쓰기</button>
            )}
          </div>
        )}

        {/* 더 보기 */}
        {!loading && hasMore && (
          <button onClick={loadMore} style={{
            marginTop: 4, padding: '12px', border: '1px dashed var(--cs-line-2)', borderRadius: 12,
            fontSize: 12.5, color: 'var(--cs-ink-2)', background: 'transparent', cursor: 'pointer', fontWeight: 600,
          }}>더 보기 ({displayPosts.length}/{total + visibleBots.length})</button>
        )}
        {loading && displayPosts.length > 0 && (
          <div style={{ textAlign: 'center', fontSize: 11.5, color: 'var(--cs-mute)', padding: 8 }}>불러오는 중…</div>
        )}
      </div>

      {/* 글쓰기 FAB */}
      <button aria-label="글쓰기" onClick={() => navigate('/community/write')} style={{
        position: 'fixed', right: 18, bottom: 'calc(86px + env(safe-area-inset-bottom, 0px))',
        width: 52, height: 52, borderRadius: 999,
        background: 'var(--cs-accent)', color: 'var(--cs-gold-on-ink)',
        border: 0, cursor: 'pointer', boxShadow: 'var(--cs-shadow-3)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 90,
      }}>
        <CSIcon name="plus" size={22} stroke={2.2} />
      </button>
      </>}
    </div>
  );
}
